import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { landingPathForRole } from '../utils/impersonationSession';
import { setUserInfo } from '../api/client';

/**
 * Handles impersonation callback from super admin.
 * Receives token and user data via URL, stores user info, and redirects.
 */
const ImpersonateCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleImpersonation = () => {
      const token = searchParams.get('token');
      const encodedData = searchParams.get('data');

      if (!token) {
        setError('No impersonation token provided');
        return;
      }

      if (!encodedData) {
        setError('No user data provided');
        return;
      }

      try {
        // Decode user data from URL (Unicode-safe base64)
        const decoded = atob(encodedData);
        const jsonStr = decodeURIComponent(Array.from(decoded, c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        const userData = JSON.parse(jsonStr);

        // Store user info with the impersonation token
        const userInfo = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
          memberships: userData.memberships || [],
          currentMembership: userData.currentMembership || null,
          isSuperAdmin: userData.isSuperAdmin || false,
          token: token,
        };

        setUserInfo(userInfo);
        localStorage.setItem('impersonating', 'true');

        // Land on the surface the target's role can actually use — dropping a
        // member on /admin/* shows a page where every request 403s. Direct
        // navigation so AuthContext reloads from the stored session.
        window.location.href = landingPathForRole(userData.currentMembership?.role);
      } catch (err: any) {
        console.error('Impersonation error:', err);
        setError('Failed to process impersonation data');
      }
    };

    handleImpersonation();
  }, [searchParams]);

  if (error) {
    return (
      <div className="page-content">
        <div className="form-container">
          <h2>Impersonation Failed</h2>
          <p className="error-message">{error}</p>
          <p>Please contact support if this issue persists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        <h2>Authenticating...</h2>
        <p>Please wait while we set up your session.</p>
      </div>
    </div>
  );
};

export default ImpersonateCallback;
